import React from "react";
import { motion } from "framer-motion";
import CreateAccountModal from "components/CreateAccountModal";
import DownloadModal from "components/DownloadModal";
import MobileDownloadModal from "components/MobileDownloadModal";
import { useSignupForm } from "hooks/useSignupForm";
import TurnstileWidget, {
  TurnstileWidgetHandle,
} from "components/TurnstileWidget";

import styles from "./styles.module.scss";

import BackgroundDesktop from "assets/img-winaz/29k-landing/bgngang.webp";
import BackgroundMobile from "assets/img-winaz/29k-landing/bgdoc.webp";

// Banner top (giữ nguyên từ 29K)
import LandingBanner from "assets/img-winaz/29k-landing/landingdoc.webp";
import LogoWinaz from "assets/img-winaz/29k-landing/logowwinaz.webp";

// Side banner trái
import BannerLeft from "assets/img-winaz/x3-von-landing/banner-chuyentien.webp";

// Tiêu đề
import TittleImg from "assets/img-winaz/x3-von-landing/tittle.webp";

// Form inputs
import BgCloudflare from "assets/img-winaz/29k-landing/bgcloudflare.webp";

// Button đăng ký: khung + text nhân 3 vốn ngay
import BtnFrame from "assets/img-winaz/tan-thu-landing/button.webp";
import BtnTextX3 from "assets/img-winaz/x3-von-landing/x3vonngay.webp";
import BtnIconX3 from "assets/img-winaz/tan-thu-landing/x3ic.webp";

// Ưu đãi cards
import BguUdai from "assets/img-winaz/29k-landing/bguudai.webp";
// import UdaiIcon1 from "assets/img-winaz/29k-landing/icon1.webp";
import UdaiIcon1 from "assets/img-winaz/tan-thu-landing/thuongtoida.webp";
import UdaiIcon2 from "assets/img-winaz/tan-thu-landing/khogamdadang.webp";
import UdaiIcon3 from "assets/img-winaz/vip/hoantra.webp";
import DotImg from "assets/img-winaz/29k-landing/dot.webp";

// Bottom buttons
import BtnChoiBanWeb from "assets/img-winaz/29k-landing/btnchoibanwweb.webp";
import LbelRutTien from "assets/img-winaz/tan-thu-landing/naprutnhanh.webp";
import LbelHoTro from "assets/img-winaz/29k-landing/lbelhotro247.webp";

// Social / liên hệ
import DashImg from "assets/img-winaz/29k-landing/dash.png";
import SocialFacebook from "assets/img-winaz/29k-landing/iconfacebook.webp";
import SocialTelegram from "assets/img-winaz/29k-landing/icontelegram.webp";
import BtnTaiGame from "assets/img-winaz/29k-landing/icondownload.webp";

// Form inputs
import KhungInput from "assets/img-winaz/live-landing/khunginput.webp";
import IconUser from "assets/img-winaz/live-landing/iconuser.webp";
import IconLock from "assets/img-winaz/live-landing/iconlock.webp";

// Stat cards
import IconJackpot from "assets/img-winaz/live-landing/iconjp.webp";
import IconCup from "assets/img-winaz/live-landing/iconcup.webp";
import IconGift from "assets/img-winaz/live-landing/icongifft.webp";
import IconOnline from "assets/img-winaz/live-landing/iconusser.webp";
import IconMoney from "assets/img-winaz/live-landing/icontien.webp";
import IconLiveBadge from "assets/img-winaz/live-landing/iconlive.webp";

import { useLiveStats, formatWin } from "./useLiveStats";
import { SOCIAL_LINKS } from "const";
import { getPlayGameDomain } from "utils";
import { flushEngagementThen } from "utils/dataLayer";

type Platform = "android" | "ios" | "unknown";

const detectPlatform = (userAgent: string): Platform => {
  if (/android/i.test(userAgent)) return "android";
  if (/iPhone|iPad|iPod|iOS/i.test(userAgent)) return "ios";
  return "unknown";
};

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

const INTERCOM_APP_ID = "snx821c4";
type IntercomModule = typeof import("@intercom/messenger-js-sdk");

const INTERACTIVE_IMAGE_PROPS = {
  initial: { filter: "brightness(1)", scale: 1 },
  whileHover: { filter: "brightness(1.08)", scale: 1.03 },
  whileTap: { scale: 0.96 },
  transition: {
    type: "spring" as const,
    stiffness: 450,
    damping: 32,
    mass: 0.28,
  },
};

const UDAI_CARDS = [
  {
    id: "thuong-toi-ta",
    icon: UdaiIcon1,
    label: "Thưởng tối ta",
    value: "+8888K",
    valueClass: "orange",
  },
  {
    id: "kho-game",
    icon: UdaiIcon2,
    label: "Kho game đa dạng",
    value: "+50 GAME",
    valueClass: "green",
  },
  {
    id: "hoan-tra",
    icon: UdaiIcon3,
    label: "Hoàn trả khủng tới",
    value: "20%",
    valueClass: "yellow",
  },
];

const Home: React.FunctionComponent = () => {
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    React.useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const [isMobileDownloadModalOpen, setIsMobileDownloadModalOpen] =
    React.useState(false);
  const [mobilePlatform, setMobilePlatform] = React.useState<"android" | "ios">(
    "android",
  );

  const liveStats = useLiveStats();
  const intercomModuleRef = React.useRef<Promise<IntercomModule | null> | null>(
    null,
  );
  const turnstileWidgetRef = React.useRef<TurnstileWidgetHandle | null>(null);
  const hasTrackedInlineFormStartRef = React.useRef(false);
  const usernameInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleResetTurnstile = React.useCallback(() => {
    turnstileWidgetRef.current?.reset();
  }, []);

  const pushDataLayerEvent = React.useCallback(
    (eventName: string, payload: Record<string, any> = {}) => {
      if (typeof window === "undefined") return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...payload });
    },
    [],
  );

  const trackInlineFormStart = React.useCallback(() => {
    if (hasTrackedInlineFormStartRef.current) return;
    hasTrackedInlineFormStartRef.current = true;
    pushDataLayerEvent("lp_form_start", { form_id: "register_inline" });
  }, [pushDataLayerEvent]);

  const {
    username,
    password,
    apiError,
    usernameError,
    passwordError,
    isSubmitting,
    turnstileRequired,
    handleSubmit: handleInlineSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleTurnstileTokenChange,
  } = useSignupForm({
    requireConfirmPassword: false,
    formId: "register_inline",
    onTurnstileReset: handleResetTurnstile,
  });

  const loadIntercom = React.useCallback(() => {
    if (typeof window === "undefined") return Promise.resolve(null);
    if (!intercomModuleRef.current) {
      intercomModuleRef.current = import("@intercom/messenger-js-sdk")
        .then((module) => {
          module.default({ app_id: INTERCOM_APP_ID });
          return module;
        })
        .catch((error) => {
          console.error("Failed to load Intercom messenger", error);
          intercomModuleRef.current = null;
          return null;
        });
    }
    return intercomModuleRef.current;
  }, []);

  React.useEffect(() => {
    loadIntercom();
  }, [loadIntercom]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleNavigate = React.useCallback(
    (url: string, target: "_self" | "_blank" = "_self") => {
      if (typeof window === "undefined") return;
      if (target === "_blank") {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      void flushEngagementThen(url);
    },
    [],
  );

  const handleLogoClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_logo", { location: "header" });
    usernameInputRef.current?.focus();
  }, [pushDataLayerEvent]);

  const handleCloseCreateAccount = React.useCallback(() => {
    setIsCreateAccountModalOpen(false);
  }, []);

  const handleOpenDownloadModal = React.useCallback(() => {
    setIsDownloadModalOpen(true);
  }, []);

  const handleCloseDownloadModal = React.useCallback(() => {
    setIsDownloadModalOpen(false);
  }, []);

  const handleOpenMobileDownloadModal = React.useCallback(
    (platform: "android" | "ios") => {
      setMobilePlatform(platform);
      setIsMobileDownloadModalOpen(true);
    },
    [],
  );

  const handleCloseMobileDownloadModal = React.useCallback(() => {
    setIsMobileDownloadModalOpen(false);
  }, []);

  const handleDownloadClick = React.useCallback(() => {
    if (typeof window === "undefined") return;
    const platform = detectPlatform(navigator.userAgent);
    pushDataLayerEvent("lp_click_cta_download", {
      platform: platform !== "unknown" ? platform : "android/ios",
      section: "bottom",
    });
    if (platform === "android") {
      handleOpenMobileDownloadModal("android");
    } else if (platform === "ios") {
      handleOpenMobileDownloadModal("ios");
    } else {
      handleOpenDownloadModal();
    }
  }, [
    handleOpenDownloadModal,
    handleOpenMobileDownloadModal,
    pushDataLayerEvent,
  ]);

  const handlePlayWebClick = React.useCallback(() => {
    pushDataLayerEvent("lp_click_cta_playweb", { section: "top" });
    handleNavigate(getPlayGameDomain());
  }, [handleNavigate, pushDataLayerEvent]);

  const handleSocialClick = React.useCallback(
    (
      platformId: string,
      url: string,
      target: "_self" | "_blank" = "_self",
      location: "hero" | "desktop" | "footer",
    ) => {
      const eventName =
        platformId === "telegram"
          ? "lp_click_social_Telegram"
          : "lp_click_social_FB";
      pushDataLayerEvent(eventName, { social_platform: platformId, location });
      handleNavigate(url, target);
    },
    [handleNavigate, pushDataLayerEvent],
  );

  const statsCards = [
    {
      id: "jackpot",
      icon: IconJackpot,
      label: "Jackpot đang tích lũy",
      value: formatWin(liveStats.jackpot),
      valueClass: styles.statValueYellow,
      live: false,
    },
    {
      id: "winner",
      icon: IconCup,
      label: (
        <>
          <strong>{liveStats.winnerName}</strong> vừa thắng
        </>
      ),
      value: formatWin(liveStats.winnerPrize),
      valueClass: styles.statValueYellow,
      live: true,
    },
    {
      id: "code",
      icon: IconGift,
      label: (
        <>
          <strong>{liveStats.codeName}</strong> vừa nhận code
        </>
      ),
      value: `+${formatWin(liveStats.codePrize)}`,
      valueClass: styles.statValueGreen,
      live: false,
    },
    {
      id: "online",
      icon: IconOnline,
      label: "Đang chơi",
      value: `${liveStats.online.toLocaleString("vi-VN")} người online`,
      valueClass: styles.statValueYellow,
      live: false,
    },
    {
      id: "paid",
      icon: IconMoney,
      label: "Đã chi thưởng hôm nay",
      value: formatWin(liveStats.paidToday),
      valueClass: styles.statValueYellow,
      live: false,
    },
  ];

  return (
    <div className={styles.home}>
      <img
        className={styles.heroBackgroundDesktop}
        src={BackgroundDesktop}
        alt=""
        aria-hidden="true"
      />
      <img
        className={styles.heroBackgroundMobile}
        src={BackgroundMobile}
        alt=""
        aria-hidden="true"
      />

      <div className={styles.canvas}>
        {/* === Banner top: landingdoc + logo overlay (giữ nguyên 29K) === */}
        <button
          type="button"
          className={styles.bannerBlock}
          onClick={handleLogoClick}
          aria-label="WinAZ"
        >
          <img
            src={LandingBanner}
            alt="WinAZ - Cổng game đổi thưởng"
            className={styles.bannerImg}
          />
          <img src={LogoWinaz} alt="WinAZ" className={styles.bannerLogo} />
        </button>

        {/* === Banner trái (chỉ landscape) === */}
        <div className={styles.sideBanner}>
          <img src={BannerLeft} alt="WinAZ - Chuyển tiền" />
        </div>

        {/* === Tiêu đề === */}
        <img
          src={TittleImg}
          alt="Khuyến mãi tân thủ"
          className={styles.tittleImg}
        />

        {/* === Form đăng ký === */}
        <form className={styles.loginCard} onSubmit={handleInlineSubmit}>
          <div className={styles.loginFields}>
            <div className={styles.fieldGroup}>
              <label
                className={styles.inputWrapper}
                style={{ backgroundImage: `url(${KhungInput})` }}
              >
                <img src={IconUser} alt="" aria-hidden="true" />
                <input
                  ref={usernameInputRef}
                  type="text"
                  placeholder="Tên đăng nhập"
                  value={username}
                  onChange={handleUsernameChange}
                  onFocus={trackInlineFormStart}
                  disabled={isSubmitting}
                />
              </label>
              {usernameError ? (
                <div className={styles.inlineError} aria-live="assertive">
                  Vui lòng nhập tên đăng nhập
                </div>
              ) : null}
            </div>
            <div className={styles.fieldGroup}>
              <label
                className={styles.inputWrapper}
                style={{ backgroundImage: `url(${KhungInput})` }}
              >
                <img src={IconLock} alt="" aria-hidden="true" />
                <input
                  type="password"
                  placeholder="Mật khẩu"
                  value={password}
                  onChange={handlePasswordChange}
                  onFocus={trackInlineFormStart}
                  disabled={isSubmitting}
                />
              </label>
              {passwordError ? (
                <div className={styles.inlineError} aria-live="assertive">
                  Vui lòng nhập mật khẩu
                </div>
              ) : null}
            </div>
            {turnstileRequired ? (
              <div className={styles.turnstileGroup}>
                <div
                  className={styles.turnstileBgWrapper}
                  style={{ backgroundImage: `url(${BgCloudflare})` }}
                >
                  <div className={styles.turnstileBlock}>
                    <div className={styles.turnstileInner}>
                      <TurnstileWidget
                        ref={turnstileWidgetRef}
                        className={styles.turnstileWidget}
                        onTokenChange={handleTurnstileTokenChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {apiError ? (
              <div className={styles.inlineError} aria-live="assertive">
                {apiError}
              </div>
            ) : null}
          </div>

          {/* Button: khung + text "NHÂN 3 VỐN NGAY" + icon x3 */}
          <motion.button
            type="submit"
            className={styles.registerButtonWrapper}
            disabled={isSubmitting}
            {...INTERACTIVE_IMAGE_PROPS}
            data-cta
          >
            <img
              src={BtnFrame}
              alt="x3 vốn ngay"
              className={styles.registerButtonFrame}
            />
            <img
              src={BtnTextX3}
              alt="x3 vốn ngay"
              className={styles.registerButtonText}
            />
            <img
              src={BtnIconX3}
              alt="x3"
              className={styles.registerButtonX3Icon}
            />
          </motion.button>
        </form>

        {/* === 3 ưu đãi cards === */}
        <div className={styles.udaiRow}>
          {UDAI_CARDS.map((card) => (
            <div
              key={card.id}
              className={styles.udaiCard}
              style={{ backgroundImage: `url(${BguUdai})` }}
            >
              <img
                src={DotImg}
                alt=""
                aria-hidden="true"
                className={styles.udaiDot}
              />
              <div className={styles.udaiIconSlot}>
                <img
                  src={card.icon}
                  alt=""
                  aria-hidden="true"
                  className={styles.udaiIcon}
                />
              </div>
              <p className={styles.udaiLabel}>{card.label}</p>
              <p
                className={
                  card.valueClass === "green"
                    ? styles.udaiValueGreen
                    : card.valueClass === "orange"
                      ? styles.udaiValueOrange
                      : styles.udaiValueYellow
                }
              >
                {card.value}
              </p>
            </div>
          ))}
        </div>

        {/* === Chơi bản web === */}
        <div className={styles.playWebBtnOuter}>
          <motion.button
            type="button"
            className={styles.playWebBtn}
            onClick={handlePlayWebClick}
            {...INTERACTIVE_IMAGE_PROPS}
          >
            <img src={BtnChoiBanWeb} alt="Chơi bản web" />
          </motion.button>
        </div>

        {/* === Nạp/Rút + Hỗ trợ === */}
        <div className={styles.serviceRow}>
          <div className={styles.serviceBtn}>
            <img
              src={LbelRutTien}
              alt="Nạp/ Rút chỉ 1 phút"
              aria-hidden="true"
            />
          </div>
          <div className={styles.serviceBtn}>
            <img src={LbelHoTro} alt="Hỗ trợ 24/7" aria-hidden="true" />
          </div>
        </div>

        {/* === Cột thống kê phải (chỉ landscape) === */}
        <div className={styles.statsColumn}>
          {statsCards.map((card) => (
            <div key={card.id} className={styles.statCard}>
              <img
                className={styles.statIcon}
                src={card.icon}
                alt=""
                aria-hidden="true"
              />
              <div className={styles.statText}>
                <p className={styles.statLabel}>{card.label}</p>
                <p className={`${styles.statValue} ${card.valueClass}`}>
                  {card.value}
                </p>
              </div>
              {card.live ? (
                <img
                  className={styles.statLiveBadge}
                  src={IconLiveBadge}
                  alt="LIVE"
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* === Liên hệ + Social === */}
      <div className={styles.bottomContact}>
        <div className={styles.contactSection}>
          <span className={styles.contactText}>
            LIÊN HỆ ĐỂ NHẬN HỖ TRỢ VÀ CODE
          </span>
          <img
            src={DashImg}
            alt=""
            aria-hidden="true"
            className={styles.contactDash}
          />
        </div>
        <div className={styles.socialRow}>
          <motion.button
            type="button"
            className={styles.socialItem}
            onClick={handleDownloadClick}
            {...INTERACTIVE_IMAGE_PROPS}
          >
            <img
              src={BtnTaiGame}
              alt="Kênh tải game"
              className={styles.socialIcon}
            />
            <div className={styles.socialText}>
              <span>Kênh</span>
              <span>Tải Game</span>
            </div>
          </motion.button>
          <motion.button
            type="button"
            className={styles.socialItem}
            onClick={() =>
              handleSocialClick(
                "facebook",
                SOCIAL_LINKS.facebook,
                "_blank",
                "footer",
              )
            }
            {...INTERACTIVE_IMAGE_PROPS}
          >
            <img
              src={SocialFacebook}
              alt="Fanpage Facebook"
              className={styles.socialIcon}
            />
            <div className={styles.socialText}>
              <span>Fanpage</span>
              <span>Facebook</span>
            </div>
          </motion.button>
          <motion.button
            type="button"
            className={styles.socialItem}
            onClick={() =>
              handleSocialClick(
                "telegram",
                SOCIAL_LINKS.telegram,
                "_blank",
                "footer",
              )
            }
            {...INTERACTIVE_IMAGE_PROPS}
          >
            <img
              src={SocialTelegram}
              alt="Kênh Telegram"
              className={styles.socialIcon}
            />
            <div className={styles.socialText}>
              <span>Kênh</span>
              <span>Telegram</span>
            </div>
          </motion.button>
        </div>
      </div>

      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={handleCloseDownloadModal}
      />

      <MobileDownloadModal
        isOpen={isMobileDownloadModalOpen}
        onClose={handleCloseMobileDownloadModal}
        platform={mobilePlatform}
      />

      <CreateAccountModal
        isOpen={isCreateAccountModalOpen}
        onClose={handleCloseCreateAccount}
      />
    </div>
  );
};

export default Home;
